import { useTranslation } from 'react-i18next';

/**
 * The board's columns, which is what the attendance's stage now is.
 *
 * It used to be an internal vocabulary the board folded into columns for
 * display -- `lead`, `qualified`, `visit_scheduled`, `proposal`,
 * `negotiation` -- derived from recorded facts so nobody could place a
 * card themselves. Two vocabularies for one idea. `proposal` and
 * `negotiation` collapsed into one because the board already showed
 * them together.
 *
 * English, like every other machine key the backend speaks; the
 * Portuguese a broker reads comes from the locale files.
 */
export const DEAL_STAGES = [
  'new',
  'contacted',
  'visiting',
  'negotiating',
  'won',
  'lost',
  'paused',
] as const;

export type DealStage = (typeof DEAL_STAGES)[number];

const STAGE_KEYS: Record<DealStage, string> = {
  new: 'stages.new',
  contacted: 'stages.contacted',
  visiting: 'stages.visiting',
  negotiating: 'stages.negotiating',
  won: 'stages.won',
  lost: 'stages.lost',
  paused: 'stages.paused',
};

/**
 * Hook returning localized labels for every deal pipeline stage.
 *
 * @returns A map from deal stage to its translated label.
 */
export function useDealStageLabels(): Record<DealStage, string> {
  const { t } = useTranslation('features/deals');
  return Object.fromEntries(
    DEAL_STAGES.map((s) => [s, t(STAGE_KEYS[s])]),
  ) as Record<DealStage, string>;
}

export const DEAL_STAGE_COLORS: Record<DealStage, string> = {
  new: 'bg-[var(--surface-tertiary)] text-[var(--text-secondary)]',
  contacted: 'bg-[var(--status-info-bg)] text-[var(--status-info-text)]',
  visiting: 'bg-[var(--status-warning-bg)] text-[var(--status-warning-text)]',
  negotiating: 'bg-[var(--status-success-bg)] text-[var(--status-success-text)]',
  won: 'bg-[var(--status-success-bg)] text-[var(--status-success-text)]',
  lost: 'bg-[var(--status-error-bg)] text-[var(--status-error-text)]',
  paused: 'bg-[var(--status-warning-bg)] text-[var(--status-warning-text)]',
};

export type ActivePipelineStage = Exclude<DealStage, 'won' | 'lost' | 'paused'>;

export const ACTIVE_PIPELINE_STAGES: ActivePipelineStage[] = [
  'new',
  'contacted',
  'visiting',
  'negotiating',
];

export type LeaseStatus = DealStage;

export const useLeaseStatusLabels = useDealStageLabels;
